import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import {
  Box,
  Typography,
  Button,
  TextField,
  Snackbar,
  Alert,
  alpha,
  useTheme,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { updatePassword } from "aws-amplify/auth";

const securityFormSchema = z
  .object({
    currentPassword: z.string().min(8, { message: "Password must be at least 8 characters." }),
    newPassword:     z.string().min(8, { message: "Password must be at least 8 characters." }),
    confirmPassword: z.string().min(8, { message: "Password must be at least 8 characters." }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type SecurityFormValues = z.infer<typeof securityFormSchema>;

export function SecurityForm() {
  const theme = useTheme();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SecurityFormValues>({
    defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "" },
  });
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [changePassword, setChangePassword] = useState(false);

  const handlePasswordChange = async (data: SecurityFormValues) => {
    try {
      await updatePassword({ oldPassword: data.currentPassword, newPassword: data.newPassword });
      setIsError(false);
      setMessage("Password updated successfully");
      setChangePassword(false);
    } catch (error) {
      setIsError(true);
      setMessage(
        error && typeof error === "object" && "message" in error
          ? String((error as { message: unknown }).message)
          : "Failed to update password"
      );
    }
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: "0.88rem" },
  };

  return (
    <Box
      sx={{
        borderRadius: 4,
        bgcolor: "background.paper",
        boxShadow: "0 1px 3px rgba(16,24,64,0.06), 0 4px 16px rgba(16,24,64,0.08)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 3.5,
          pt: 3,
          pb: 2.5,
          borderBottom: "1px solid",
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: "1rem", color: "primary.main" }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: "0.95rem", fontWeight: 700, color: "text.primary" }}>
            Security
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            Update your password and manage security settings.
          </Typography>
        </Box>
      </Box>

      {/* Password section */}
      <Box sx={{ px: 3.5, py: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: changePassword ? 2.5 : 0,
          }}
        >
          <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: "text.primary" }}>
            Change Password
          </Typography>
          {!changePassword ? (
            <Button
              variant="outlined"
              size="small"
              onClick={() => setChangePassword(true)}
              sx={{
                fontSize: "0.78rem",
                fontWeight: 600,
                borderColor: "divider",
                color: "text.secondary",
                borderRadius: "8px",
                "&:hover": { borderColor: "primary.main", color: "primary.main", bgcolor: "transparent" },
              }}
            >
              Change Password
            </Button>
          ) : (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setChangePassword(false)}
                sx={{
                  fontSize: "0.78rem",
                  borderColor: "divider",
                  color: "text.secondary",
                  borderRadius: "8px",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleSubmit(handlePasswordChange)}
                sx={{ fontSize: "0.78rem", fontWeight: 600, borderRadius: "8px" }}
              >
                Save
              </Button>
            </Box>
          )}
        </Box>

        {changePassword && (
          <form onSubmit={handleSubmit(handlePasswordChange)}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Controller
                name="currentPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="password"
                    label="Current Password"
                    placeholder="••••••••"
                    size="small"
                    error={!!errors.currentPassword}
                    helperText={errors.currentPassword?.message}
                    sx={fieldSx}
                  />
                )}
              />
              <Controller
                name="newPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="password"
                    label="New Password"
                    placeholder="••••••••"
                    size="small"
                    error={!!errors.newPassword}
                    helperText={errors.newPassword?.message || "At least 8 characters."}
                    sx={fieldSx}
                  />
                )}
              />
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    type="password"
                    label="Confirm New Password"
                    placeholder="••••••••"
                    size="small"
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword?.message}
                    sx={fieldSx}
                  />
                )}
              />
            </Box>
          </form>
        )}
      </Box>

      <Snackbar
        open={message !== null}
        autoHideDuration={4000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={isError ? "error" : "success"}
          onClose={() => setMessage(null)}
          sx={{ fontSize: "0.82rem" }}
        >
          {message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
